/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import type { KeyboardEvent, ReactNode } from 'react';
import './PageTabs.css';

export interface PageTabItem<TValue extends string = string> {
  value: TValue;
  label: ReactNode;
  count?: ReactNode;
  disabled?: boolean;
  title?: string;
  id?: string;
  ariaControls?: string;
}

export interface PageTabsProps<TValue extends string = string> {
  items: readonly PageTabItem<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  ariaLabel: string;
  className?: string;
  endAction?: ReactNode;
  variant?: 'page' | 'segmented';
  /** Keep every tab visible by continuing on another line when space runs out. */
  overflow?: 'scroll' | 'wrap';
}

function focusAdjacentTab(event: KeyboardEvent<HTMLButtonElement>): void {
  const tabList = event.currentTarget.closest<HTMLElement>('[role="tablist"]');
  if (!tabList) return;

  const tabs = Array.from(
    tabList.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)'),
  );
  const currentIndex = tabs.indexOf(event.currentTarget);
  if (currentIndex < 0 || tabs.length < 2) return;

  const isRtl = window.getComputedStyle(tabList).direction === 'rtl';
  const previousKey = isRtl ? 'ArrowRight' : 'ArrowLeft';
  const nextKey = isRtl ? 'ArrowLeft' : 'ArrowRight';
  let nextIndex = currentIndex;

  if (event.key === previousKey) nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  else if (event.key === nextKey) nextIndex = (currentIndex + 1) % tabs.length;
  else if (event.key === 'Home') nextIndex = 0;
  else if (event.key === 'End') nextIndex = tabs.length - 1;
  else return;

  event.preventDefault();
  tabs[nextIndex]?.focus();
  tabs[nextIndex]?.click();
}

export function PageTabs<TValue extends string>({
  items,
  value,
  onChange,
  ariaLabel,
  className,
  endAction,
  variant = 'page',
  overflow = 'scroll',
}: PageTabsProps<TValue>) {
  const rootClassName = [
    'qp-page-tabs',
    variant === 'segmented' ? 'qp-page-tabs--segmented' : '',
    overflow === 'wrap' ? 'qp-page-tabs--wrap' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClassName}>
      <div className="qp-page-tabs__list" role="tablist" aria-label={ariaLabel}>
        {items.map((item) => {
          const isActive = item.value === value;
          return (
            <button
              key={item.value}
              type="button"
              role="tab"
              id={item.id}
              aria-controls={item.ariaControls}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              className={`qp-page-tabs__tab${isActive ? ' is-active' : ''}`}
              disabled={item.disabled}
              title={item.title}
              onClick={() => onChange(item.value)}
              onKeyDown={focusAdjacentTab}
            >
              <span className="qp-page-tabs__label">{item.label}</span>
              {item.count !== undefined && item.count !== null ? (
                <span className="qp-page-tabs__count">{item.count}</span>
              ) : null}
            </button>
          );
        })}
      </div>
      {endAction ? <div className="qp-page-tabs__action">{endAction}</div> : null}
    </div>
  );
}
